import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner, Modal } from "react-bootstrap";
import {
  GraduationCap,
  Video,
  FileText,
  BookOpen,
  ClipboardList,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Calendar,
  User,
  Clock,
  Play,
  MoreVertical,
  Bookmark,
  MessageCircle,
  Sparkles,
  CheckSquare,
  CheckCircle2,
  PlayCircle,
  Circle,
  Film,
  LayoutGrid,
  List,
  Code2,
  Check,
  Compass,
} from "lucide-react";
import { PageHeader } from "../../ui";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import { getSavedVideoIds } from "../../../utils/savedVideos";
import { usePlan } from "../../../contexts/PlanContext";
import { checkSmartLearningEnabled } from "../../../utils/smartLearning";
import StudentSyllabus from "../Syllabus/StudentSyllabus";
import LearningPathView from "../LearningPath/LearningPathView";
import MaterialsPanel from "./MaterialsPanel";
import AssignmentsPanel from "./AssignmentsPanel";
import ChallengesPanel from "./ChallengesPanel";
import { useTokenColors } from "../../../utils/themeColor";
import "./MyLearningPage.scss";

/**
 * MyLearningPage (fresh / non-session-based variant)
 *
 * Standalone "My Learning" experience for courses that are NOT using
 * session-based (Smart Learning Flow) learning. Mirrors the design mock:
 *   tabs · filterable recording list with progress rings · right widget rail.
 *
 * Route: /s/learning (also aliased at /s/learning-v2 — see App.js)
 *
 * Data sources (same APIs as the v1 StudentLearningHub):
 *   • GET /course-assignments/my-enrollments        — course switcher
 *   • GET /students/:id                             — single-course fallback
 *   • GET /videos/student/my-videos?batchId=        — recordings (session list)
 *   • GET /videos/batch/:id/view-status             — per-video progress
 *   • POST /videos/:id/mark-viewed                  — mark watched
 *   • GET /student/me/learning-overview?batchId=    — right rail widgets
 */

// ── Static config ───────────────────────────────────────────────────────────
const TABS = [
  { key: "classes", label: "Classes", icon: Video },
  { key: "materials", label: "Materials", icon: FileText },
  { key: "syllabus", label: "Syllabus", icon: BookOpen },
  { key: "assignments", label: "Assignments", icon: ClipboardList },
  { key: "challenges", label: "Coding Challenges", icon: Code2, feature: "codingPractice" },
];

const STATUS_META = {
  completed: { label: "Completed", icon: CheckCircle2, cls: "is-completed", ring: "#10b981" },
  "in-progress": { label: "In Progress", icon: PlayCircle, cls: "is-progress", ring: "#3b82f6" },
  "not-started": { label: "Not Started", icon: Circle, cls: "is-pending", ring: "#f59e0b" },
};

// Four donut segments — categories, so the chart ramp. `#7c5cff` was a violet
// that appears nowhere else in the app: a sixth palette of one colour.
// ⚠️ These land on an SVG `stroke` attribute, where var() does not resolve, so
// the component resolves them through useTokenColors (see themeColor.js).
const PROGRESS_TOKENS = {
  classes: "--chart-1",
  assignments: "--chart-3",
  challenges: "--chart-2",
  quizzes: "--chart-5",
};

const PAGE_SIZE = 5;
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

// ── Formatting helpers ───────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return null;
  const totalSec = Math.round(minutes * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatTimeRange = (start, end) => {
  const to12h = (hhmm) => {
    if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return "";
    const [h, m] = hhmm.split(":").map(Number);
    const ampm = h < 12 ? "AM" : "PM";
    const hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
  };
  const s = to12h(start);
  const e = to12h(end);
  if (s && e) return `${s} - ${e}`;
  return s || e || "";
};

// Map a view-status entry → { percent, status } using the v1 derivation.
const deriveStatus = (vs) => {
  const percent = vs?.percent ?? (vs?.viewed ? 100 : 0);
  const status = percent >= 100 ? "completed" : percent > 0 ? "in-progress" : "not-started";
  return { percent, status };
};

// ── Small presentational pieces ──────────────────────────────────────────────
const ProgressRing = ({ value, color, size = 56 }) => {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="ml-ring" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="ml-ring__svg">
        <circle cx={size / 2} cy={size / 2} r={r} className="ml-ring__track" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={value > 0 ? color : "transparent"}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="ml-ring__bar"
        />
      </svg>
      <span className="ml-ring__pct">{value}%</span>
    </div>
  );
};

const ProgressDonut = ({ overall, segments }) => {
  const R = 46;
  const C = 2 * Math.PI * R;
  const total = segments.reduce((a, s) => a + s.done, 0);
  let cumulative = 0;
  return (
    <div className="ml-donut">
      <svg viewBox="0 0 120 120" className="ml-donut__svg">
        <circle cx="60" cy="60" r={R} className="ml-donut__track" strokeWidth="12" fill="none" />
        {total > 0 &&
          segments.map((seg) => {
            if (seg.done <= 0) return null;
            const len = (seg.done / total) * C;
            const dashOffset = -cumulative;
            cumulative += len;
            return (
              <circle
                key={seg.key}
                cx="60"
                cy="60"
                r={R}
                fill="none"
                strokeWidth="12"
                strokeLinecap="butt"
                stroke={seg.color}
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 60 60)"
              />
            );
          })}
      </svg>
      <div className="ml-donut__center">
        <span className="ml-donut__pct">{overall}%</span>
        <span className="ml-donut__label">Overall</span>
      </div>
    </div>
  );
};

// Shared kebab menu (Watch now / Mark as watched) used by both card variants.
const SessionMenu = ({ session, onOpen, onMarkWatched }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="ml-session__menu" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="ml-icon-btn"
        aria-label="Session options"
        onClick={() => setMenuOpen((o) => !o)}
      >
        <MoreVertical size={16} />
      </button>
      {menuOpen && (
        <>
          <div className="ml-menu__backdrop" onClick={() => setMenuOpen(false)} />
          <ul className="ml-menu">
            <li
              onClick={() => {
                setMenuOpen(false);
                onOpen(session.id);
              }}
            >
              <Play size={14} /> Watch now
            </li>
            {!session.watched && (
              <li
                onClick={() => {
                  setMenuOpen(false);
                  onMarkWatched(session.id);
                }}
              >
                <CheckCircle2 size={14} /> Mark as watched
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
};

const SessionCard = ({ session, seq, onOpen, onMarkWatched }) => {
  const status = STATUS_META[session.status];
  const StatusIcon = status.icon;

  return (
    <article className="ml-session" onClick={() => onOpen(session.id)}>
      {/* Thumbnail */}
      <div className="ml-session__thumb">
        {session.thumbnailUrl ? (
          <img src={session.thumbnailUrl} alt={session.title} loading="lazy" />
        ) : (
          <div className="ml-session__thumb-ph">
            <Film size={22} />
          </div>
        )}
        {session.duration && (
          <span className="ml-session__duration">
            <Clock size={11} />
            {session.duration}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="ml-session__body">
        <div className="ml-session__heading">
          <span className="ml-session__num">{seq}</span>
          <h3 className="ml-session__title">{session.title}</h3>
        </div>

        <div className="ml-session__meta">
          <span className="ml-session__meta-item">
            <Calendar size={13} />
            {session.date}
          </span>
          <span className="ml-session__meta-item">
            <Video size={13} />
            {session.type}
          </span>
          {session.instructor && (
            <span className="ml-session__meta-item">
              <User size={13} />
              By {session.instructor}
            </span>
          )}
        </div>

        {session.description && <p className="ml-session__desc">{session.description}</p>}
      </div>

      {/* Aside: [ status + ring ] | [ menu ] */}
      <div className="ml-session__aside">
        <div className="ml-session__status-ring">
          <span className={`ml-status ${status.cls}`}>
            <StatusIcon size={13} />
            {status.label}
          </span>
          <ProgressRing value={session.progress} color={status.ring} />
        </div>

        <SessionMenu session={session} onOpen={onOpen} onMarkWatched={onMarkWatched} />
      </div>
    </article>
  );
};

// Grid variant — matches the new "My Learning" grid mock: thumbnail with a
// play affordance + duration, then title · meta · progress bar · status pill.
const SessionGridCard = ({ session, onOpen, onMarkWatched }) => {
  const status = STATUS_META[session.status];
  const StatusIcon = status.icon;

  return (
    <article className="ml-gcard" onClick={() => onOpen(session.id)}>
      <div className="ml-gcard__thumb">
        {session.thumbnailUrl ? (
          <img src={session.thumbnailUrl} alt={session.title} loading="lazy" />
        ) : (
          <div className="ml-gcard__thumb-ph">
            <Film size={26} />
          </div>
        )}
        <span className="ml-gcard__play">
          <Play size={16} fill="currentColor" />
        </span>
        {session.duration && <span className="ml-gcard__duration">{session.duration}</span>}
      </div>

      <div className="ml-gcard__body">
        <div className="ml-gcard__heading">
          <h3 className="ml-gcard__title" title={session.title}>
            {session.title}
          </h3>
          <SessionMenu session={session} onOpen={onOpen} onMarkWatched={onMarkWatched} />
        </div>

        <div className="ml-gcard__meta">
          <Calendar size={12} />
          <span>{session.date}</span>
          <span className="ml-gcard__sep">•</span>
          <span>{session.type}</span>
        </div>

        <div className="ml-gcard__progress">
          <div className="ml-gcard__bar">
            <div
              className="ml-gcard__bar-fill"
              style={{ width: `${session.progress}%`, background: status.ring }}
            />
          </div>
          <span className="ml-gcard__pct">{session.progress}%</span>
        </div>

        <span className={`ml-status ${status.cls}`}>
          <StatusIcon size={13} />
          {status.label}
        </span>
      </div>
    </article>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const MyLearningPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPlanFeature } = usePlan();
  const user = getCurrentUser();
  const studentId = user?.studentId;

  // When arriving from the dashboard "Continue Learning" button, the clicked
  // course's identifiers are passed via router state so we can pre-select the
  // matching enrollment in the course switcher (consumed once, after load).
  const preselectRef = useRef(location.state || null);

  // ── Enrollments / course switcher ──
  const [enrollments, setEnrollments] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [courseOpen, setCourseOpen] = useState(false);

  const selected = enrollments[selectedIdx] || null;
  const batchId = selected?.batchId?._id || selected?.batchId || null;
  const courseId = selected?.courseId?._id || selected?.courseId || null;
  const hasMultiple = enrollments.length > 1;

  // ── Smart Learning Flow gate (course-level) ──
  // When the selected course opts into smartLearningFlow, we surface the
  // guided "Learning Path" experience as the default tab. When it's off, the
  // page behaves exactly as before (legacy recordings view) — backward compat.
  const [smartLearningOn, setSmartLearningOn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!courseId) {
      setSmartLearningOn(false);
      return undefined;
    }
    checkSmartLearningEnabled(courseId).then((on) => {
      if (!cancelled) setSmartLearningOn(!!on);
    });
    return () => { cancelled = true; };
  }, [courseId]);

  // Hide tabs whose plan feature is disabled for this institute (fail-open);
  // prepend "Learning Path" when Smart Learning Flow is on for the course.
  const visibleTabs = useMemo(() => {
    const base = TABS.filter((t) => !t.feature || hasPlanFeature(t.feature));
    return smartLearningOn
      ? [{ key: "learning-path", label: "Learning Path", icon: Compass }, ...base]
      : base;
  }, [hasPlanFeature, smartLearningOn]);

  // ── Recordings (session list) ──
  const [recordings, setRecordings] = useState([]);
  const [courseThumbnailUrl, setCourseThumbnailUrl] = useState("");
  const [viewStatus, setViewStatus] = useState({});
  const [listLoading, setListLoading] = useState(true);

  // ── Course-level videos (shown for the selected course, distinct from
  // batch session recordings) + their lightweight stream-modal player ──
  const [courseVideos, setCourseVideos] = useState([]);
  const [playingCourseVideo, setPlayingCourseVideo] = useState(null);
  const [cvStreamUrl, setCvStreamUrl] = useState(null);
  const [cvStreamLoading, setCvStreamLoading] = useState(false);

  // ── Right-rail overview ──
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // ── UI state (tabs + filters) ──
  // A link can open a tab by name (`state: { tab }`), which is what "View all
  // assignments" on the dashboard does.
  const linkedTab = TABS.some((t) => t.key === location.state?.tab) ? location.state.tab : null;
  const [activeTab, setActiveTab] = useState(linkedTab || "classes");
  const linkedTabRef = useRef(!!linkedTab);

  // Track whether the student has explicitly picked a tab this course-view so
  // we don't yank them off it when the smartLearningFlow probe resolves.
  const userChoseTabRef = useRef(false);
  useEffect(() => { userChoseTabRef.current = false; }, [courseId]);

  // Make Learning Path the default landing tab for a smart-learning course
  // (unless the student already navigated to another tab, or arrived on one).
  useEffect(() => {
    if (!smartLearningOn) return;
    if (linkedTabRef.current) {
      linkedTabRef.current = false;
      return;
    }
    if (!userChoseTabRef.current) setActiveTab("learning-path");
  }, [smartLearningOn]);

  // Keep activeTab valid as the tab set changes (flag flip / course switch).
  useEffect(() => {
    const keys = visibleTabs.map((t) => t.key);
    if (!keys.includes(activeTab)) setActiveTab(keys[0]);
  }, [visibleTabs, activeTab]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [savedOnly, setSavedOnly] = useState(false);
  const [savedIds, setSavedIds] = useState(() => getSavedVideoIds(studentId));
  const [sort, setSort] = useState("recent");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("vc-ml-view") === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });

  // Persist the chosen layout so it sticks across visits.
  useEffect(() => {
    try {
      localStorage.setItem("vc-ml-view", viewMode);
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [viewMode]);

  // ── Load enrollments (once) ──
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setProfileLoading(true);
      setProfileError("");
      try {
        const { data } = await apiClient.get("/course-assignments/my-enrollments");
        const list = data?.data || [];
        if (list.length > 0) {
          if (!cancelled) {
            setEnrollments(list);
            setProfileLoading(false);
          }
          return;
        }
      } catch { /* fall through */ }

      // Fallback: single course from student profile
      try {
        if (!studentId) throw new Error();
        const { data } = await apiClient.get(`/students/${studentId}`);
        const student = data.student;
        if (student?.batch) {
          const bId = student.batch._id || student.batch;
          const cId =
            student.batch?.course?._id || student.batch?.course || student.course?._id || student.course;
          const courseName = student.batch?.course?.name || student.course?.name || "";
          const courseCode = student.batch?.course?.code || student.course?.code || "";
          const batchName = student.batch?.name || "";
          if (!cancelled) {
            setEnrollments([
              {
                _id: "fallback",
                batchId: { _id: bId, name: batchName },
                courseId: { _id: cId, name: courseName, code: courseCode },
                isPrimary: true,
              },
            ]);
          }
        } else if (!cancelled) {
          setProfileError("You are not enrolled in any batch yet.");
        }
      } catch {
        if (!cancelled) setProfileError("Failed to load your batch information.");
      }
      if (!cancelled) setProfileLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [studentId]);

  // ── Apply the dashboard pre-selection once enrollments are available ──
  // Match on batchId first (most specific), then fall back to courseId.
  // The ref is cleared after the first run so a later course switch sticks.
  useEffect(() => {
    const pre = preselectRef.current;
    if (!pre || enrollments.length === 0) return;
    preselectRef.current = null;
    const { preselectBatchId, preselectCourseId } = pre;
    let idx = -1;
    if (preselectBatchId) {
      idx = enrollments.findIndex(
        (e) => String(e.batchId?._id || e.batchId) === String(preselectBatchId)
      );
    }
    if (idx < 0 && preselectCourseId) {
      idx = enrollments.findIndex(
        (e) => String(e.courseId?._id || e.courseId) === String(preselectCourseId)
      );
    }
    if (idx > 0) setSelectedIdx(idx);
  }, [enrollments]);

  // ── Load recordings + view-status when the selected batch changes ──
  useEffect(() => {
    let cancelled = false;
    const fetchRecordings = async () => {
      setListLoading(true);
      try {
        const params = batchId ? `?batchId=${batchId}` : "";
        const { data } = await apiClient.get(`/videos/student/my-videos${params}`);
        const vids = data?.data || [];
        if (cancelled) return;
        setRecordings(vids);
        setCourseThumbnailUrl(data?.courseThumbnailUrl || "");

        const resolvedBatchId = batchId || vids[0]?.batch?._id || vids[0]?.batch;
        if (resolvedBatchId) {
          try {
            const vsRes = await apiClient.get(`/videos/batch/${resolvedBatchId}/view-status`);
            if (!cancelled) setViewStatus(vsRes.data?.data || {});
          } catch { /* non-fatal */ }
        }
      } catch {
        if (!cancelled) setRecordings([]);
      }
      if (!cancelled) setListLoading(false);
    };

    // Wait until enrollments resolve before fetching (avoids a flash of empty).
    if (!profileLoading) fetchRecordings();
    return () => { cancelled = true; };
  }, [batchId, profileLoading]);

  // ── Load right-rail overview when the selected batch changes ──
  useEffect(() => {
    let cancelled = false;
    setOverviewLoading(true);
    const params = batchId ? `?batchId=${batchId}` : "";
    apiClient
      .get(`/student/me/learning-overview${params}`)
      .then((res) => { if (!cancelled) setOverview(res.data || null); })
      .catch(() => { if (!cancelled) setOverview(null); })
      .finally(() => { if (!cancelled) setOverviewLoading(false); });
    return () => { cancelled = true; };
  }, [batchId]);

  // Reset to page 1 whenever a filter (or the page-size-changing view) changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter, sessionFilter, sort, query, selectedIdx, viewMode, savedOnly]);

  const handleSelectCourse = useCallback((idx) => {
    setSelectedIdx(idx);
    setCourseOpen(false);
  }, []);

  // Pass the selected batch (+ course label) so the player resolves context
  // directly instead of re-discovering which batch the video belongs to.
  const openRecording = useCallback(
    (id) => navigate(`/s/watch-v2/${id}`, {
      state: {
        batchId,
        courseName: selected?.courseId?.name || "",
        courseCode: selected?.courseId?.code || selected?.batchId?.name || "",
      },
    }),
    [navigate, batchId, selected]
  );

  const markWatched = useCallback(async (id) => {
    setViewStatus((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), viewed: true, percent: 100 } }));
    try {
      await apiClient.post(`/videos/${id}/mark-viewed`);
    } catch {
      setViewStatus((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), viewed: false } }));
    }
  }, []);

  // ── Map recordings → session cards (sorted, sequenced, filtered) ──
  const sessions = useMemo(() => {
    let list = recordings.map((rec) => {
      const vs = viewStatus[rec._id];
      const { percent, status } = deriveStatus(vs);
      return {
        id: rec._id,
        title: rec.topic || rec.title || "Recording",
        date: formatDate(rec.recordedAt),
        recordedAt: rec.recordedAt,
        type: "Class",
        instructor: rec.uploadedBy?.name || "",
        description: rec.description || "",
        status,
        progress: percent,
        duration: formatDuration(rec.duration),
        thumbnailUrl: courseThumbnailUrl || rec.thumbnailUrl || "",
        watched: !!vs?.viewed,
        topic: (rec.topic || rec.title || "").trim(),
      };
    });

    list.sort((a, b) => {
      const da = new Date(a.recordedAt).getTime();
      const db = new Date(b.recordedAt).getTime();
      return sort === "recent" ? db - da : da - db;
    });
    // Stable sequence number follows the sorted order (newest = 1 for "recent")
    list = list.map((s, i) => ({ ...s, seq: i + 1 }));

    if (statusFilter !== "all") list = list.filter((s) => s.status === statusFilter);
    if (sessionFilter !== "all") list = list.filter((s) => s.topic === sessionFilter);
    if (savedOnly) list = list.filter((s) => savedIds.includes(s.id));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (s) => s.title.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [recordings, viewStatus, courseThumbnailUrl, sort, statusFilter, sessionFilter, savedOnly, savedIds, query]);

  // Distinct topics for the "All Sessions" dropdown
  const sessionOptions = useMemo(() => {
    const seen = new Set();
    for (const rec of recordings) {
      const t = (rec.topic || rec.title || "").trim();
      if (t) seen.add(t);
    }
    return ["all", ...Array.from(seen)];
  }, [recordings]);

  // Grid packs more per page (two rows of four) than the taller list rows.
  const pageSize = viewMode === "grid" ? 8 : PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(sessions.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = sessions.slice((safePage - 1) * pageSize, safePage * pageSize);

  // ── Right-rail data ──
  const cont = overview?.continueLearning;
  const upcoming = overview?.upcoming || [];
  const progress = overview?.progress;
  const progressColors = useTokenColors(PROGRESS_TOKENS);
  const progressSegments = progress
    ? Object.keys(PROGRESS_TOKENS).map((key) => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
        color: progressColors[key],
        done: progress[key]?.done || 0,
        total: progress[key]?.total || 0,
      }))
    : [];

  // Switch to the Classes tab filtered to the student's saved (watch-later) list.
  const showSavedVideos = useCallback(() => {
    setSavedIds(getSavedVideoIds(studentId));
    setSavedOnly(true);
    setActiveTab("classes");
  }, [studentId]);

  const quickAccess = [
    // Notes opens the in-page Materials tab; Saved videos filters Classes to the
    // student's watch-later list; Batch chat + Ask AI (AIvo) are fully wired.
    { key: "notes", label: "Notes", icon: CheckSquare, token: "--chart-1", go: () => setActiveTab("materials") },
    { key: "saved", label: "Saved videos", icon: Bookmark, token: "--chart-3", go: showSavedVideos },
    { key: "chat", label: "Batch chat", icon: MessageCircle, token: "--chart-2", go: () => navigate("/s/chat") },
    {
      key: "ai",
      label: "Ask AI",
      icon: Sparkles,
      token: "--chart-5",
      // Open the floating AIvo chat box; fall back to the AI learning page for
      // institutes whose plan doesn't mount the AIvo widget.
      go: () =>
        hasPlanFeature("aiDoubtSolving")
          ? window.dispatchEvent(new CustomEvent("vc-open-aivo"))
          : navigate("/s/learn-with-ai"),
    },
  ];

  // The right rail (continue/upcoming/progress) is only meaningful alongside
  // the class recordings; other tabs use the full width for their content grids.
  const showRail = !profileLoading && !!batchId && activeTab === "classes";

  // ── Course switcher (sits at the right end of the tab row) ──
  const courseSwitcher = selected ? (
    <div className="ml-course">
      <button
        type="button"
        className={`ml-course__trigger${hasMultiple ? "" : " is-static"}`}
        onClick={() => hasMultiple && setCourseOpen((o) => !o)}
        aria-haspopup={hasMultiple ? "listbox" : undefined}
        aria-expanded={hasMultiple ? courseOpen : undefined}
        disabled={!hasMultiple}
        title={selected?.courseId?.name || "Course"}
      >
        <span className="ml-course__badge">
          <BookOpen size={15} />
        </span>
        <span className="ml-course__info">
          <span className="ml-course__eyebrow">Course</span>
          <span className="ml-course__name">{selected?.courseId?.name || "Select Course"}</span>
        </span>
        {hasMultiple && (
          <ChevronDown size={16} className={`ml-course__chevron ${courseOpen ? "is-open" : ""}`} />
        )}
      </button>
      {hasMultiple && courseOpen && (
        <>
          <div className="ml-menu__backdrop" onClick={() => setCourseOpen(false)} />
          <ul className="ml-course__dropdown" role="listbox" aria-label="Select course">
            {enrollments.map((enr, i) => (
              <li
                key={enr._id}
                role="option"
                aria-selected={i === selectedIdx}
                className={i === selectedIdx ? "is-active" : ""}
                onClick={() => handleSelectCourse(i)}
              >
                <span className="ml-course__opt-badge">
                  <BookOpen size={14} />
                </span>
                <span className="ml-course__opt-text">
                  <span className="ml-course__opt-name">{enr.courseId?.name || "Course"}</span>
                  {(enr.courseId?.code || enr.batchId?.name) && (
                    <span className="ml-course__opt-code">{enr.courseId?.code || enr.batchId?.name}</span>
                  )}
                </span>
                {i === selectedIdx && <Check size={16} className="ml-course__opt-check" />}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  ) : null;

  // ── Load the selected course's course-level videos ──
  // These are course-scoped video materials (not tied to a batch session).
  // Shown in the Classes tab with a distinguishing "Course Video" badge.
  useEffect(() => {
    let cancelled = false;
    if (!courseId) { setCourseVideos([]); return undefined; }
    apiClient
      .get(`/materials/course/${courseId}`, { params: { limit: 100 } })
      .then((res) => {
        if (cancelled) return;
        const vids = (res.data?.data || []).filter(
          (m) => m.isVideo && m.status === "ready" && !m.isDeleted,
        );
        setCourseVideos(vids);
      })
      .catch(() => { if (!cancelled) setCourseVideos([]); });
    return () => { cancelled = true; };
  }, [courseId]);

  // Open a course-level video in the lightweight stream modal (download
  // disabled). Course videos are BatchMaterial docs, streamed via /materials.
  const openCourseVideo = useCallback(async (video) => {
    setPlayingCourseVideo(video);
    setCvStreamUrl(null);
    setCvStreamLoading(true);
    try {
      const res = await apiClient.get(`/materials/${video._id}/stream`);
      if (res.data?.success) setCvStreamUrl(res.data.data.url);
    } catch {
      setPlayingCourseVideo(null);
    }
    setCvStreamLoading(false);
  }, []);

  // ── Classes panel body ──
  const renderClasses = () => {
    if (listLoading) {
      return (
        <div className="ml-loading">
          <Spinner animation="border" />
        </div>
      );
    }
    if (profileError || !batchId) {
      return (
        <div className="ml-empty">
          <Video size={32} />
          <p>{profileError || "You are not enrolled in any batch yet."}</p>
        </div>
      );
    }
    const courseVideosSection = courseVideos.length > 0 ? (
      <section className="ml-coursevids">
        <div className="ml-coursevids__head">
          <h3 className="ml-coursevids__title">Course Videos</h3>
          <span className="ml-coursevids__hint">
            Available across all batches of this course
          </span>
        </div>
        <div className="ml-grid">
          {courseVideos.map((v) => (
            <article key={v._id} className="ml-gcard" onClick={() => openCourseVideo(v)}>
              <div className="ml-gcard__thumb">
                <div className="ml-gcard__thumb-ph"><Film size={26} /></div>
                <span className="ml-gcard__play"><Play size={16} fill="currentColor" /></span>
                {formatDuration(v.duration) && (
                  <span className="ml-gcard__duration">{formatDuration(v.duration)}</span>
                )}
              </div>
              <div className="ml-gcard__body">
                <div className="ml-gcard__heading">
                  <h3 className="ml-gcard__title" title={v.title}>{v.title}</h3>
                </div>
                <span className="ml-cv-badge"><BookOpen size={12} /> Course Video</span>
              </div>
            </article>
          ))}
        </div>
      </section>
    ) : null;

    if (recordings.length === 0) {
      return (
        <>
          {courseVideosSection}
          {courseVideos.length === 0 && (
            <div className="ml-empty">
              <Video size={32} />
              <p>Class recordings will appear here once your instructor records a session.</p>
            </div>
          )}
        </>
      );
    }

    return (
      <>
        {courseVideosSection}
        {/* Filter bar */}
        <div className="ml-filters">
          <div className="ml-filters__left">
            <div className="ml-select">
              <select value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)} aria-label="Session filter">
                {sessionOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === "all" ? "All Sessions" : opt}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} className="ml-select__chevron" />
            </div>
            <div className="ml-select">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status filter">
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="in-progress">In Progress</option>
                <option value="not-started">Not Started</option>
              </select>
              <ChevronDown size={15} className="ml-select__chevron" />
            </div>
            <button
              type="button"
              className={`ml-savedtoggle${savedOnly ? " is-active" : ""}`}
              onClick={() => {
                setSavedIds(getSavedVideoIds(studentId));
                setSavedOnly((s) => !s);
              }}
              aria-pressed={savedOnly}
              title="Show saved videos"
            >
              <Bookmark size={14} fill={savedOnly ? "currentColor" : "none"} />
              Saved
            </button>
          </div>

          <div className="ml-filters__right">
            <div className="ml-select">
              <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
                <option value="recent">Recent First</option>
                <option value="oldest">Oldest First</option>
              </select>
              <ChevronDown size={15} className="ml-select__chevron" />
            </div>
            <div className="ml-search">
              <Search size={15} className="ml-search__icon" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="ml-viewtoggle" role="group" aria-label="View layout">
              <button
                type="button"
                className={`ml-viewtoggle__btn${viewMode === "grid" ? " is-active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
                aria-pressed={viewMode === "grid"}
              >
                <LayoutGrid size={16} />
              </button>
              <button
                type="button"
                className={`ml-viewtoggle__btn${viewMode === "list" ? " is-active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label="List view"
                aria-pressed={viewMode === "list"}
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Session list / grid */}
        {pageItems.length > 0 ? (
          viewMode === "grid" ? (
            <div className="ml-grid">
              {pageItems.map((s) => (
                <SessionGridCard
                  key={s.id}
                  session={s}
                  onOpen={openRecording}
                  onMarkWatched={markWatched}
                />
              ))}
            </div>
          ) : (
            <div className="ml-list">
              {pageItems.map((s) => (
                <SessionCard
                  key={s.id}
                  session={s}
                  seq={s.seq}
                  onOpen={openRecording}
                  onMarkWatched={markWatched}
                />
              ))}
            </div>
          )
        ) : (
          <div className="ml-empty">
            {savedOnly ? <Bookmark size={32} /> : <Video size={32} />}
            <p>
              {savedOnly
                ? "No saved videos yet. Tap the bookmark on a video to add it to your watch-later list."
                : "No sessions match your filters."}
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="ml-pager" aria-label="Pagination">
            <button
              type="button"
              className="ml-pager__arrow"
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            {buildPageList(safePage, totalPages).map((p, idx) =>
              p === "…" ? (
                <span key={`gap-${idx}`} className="ml-pager__gap">…</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`ml-pager__page${p === safePage ? " is-active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              type="button"
              className="ml-pager__arrow"
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </nav>
        )}
      </>
    );
  };

  return (
    <div className="my-learning">
      <PageHeader
        title="My Learning"
        description="Access your class recordings, study materials, and more"
        icon={GraduationCap}
      />

      <div className="ml-card">
        {/* Tabs + course switcher */}
        <div className="ml-tabrow">
          <div className="ml-tabrow__tabs" role="tablist">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const active = tab.key === activeTab;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`ml-tab${active ? " is-active" : ""}`}
                  onClick={() => {
                    userChoseTabRef.current = true;
                    setActiveTab(tab.key);
                  }}
                >
                  <Icon size={15} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
          {courseSwitcher}
        </div>

        {/* Body: main content + (classes-only) right rail */}
        <div className={`ml-body${showRail ? "" : " ml-body--full"}`}>
          <div className="ml-body__main">
            {activeTab === "learning-path" &&
              (profileLoading ? (
                <div className="ml-loading">
                  <Spinner animation="border" />
                </div>
              ) : !courseId ? (
                <div className="ml-empty">
                  <Compass size={32} />
                  <p>Select a course to view your learning path.</p>
                </div>
              ) : (
                <LearningPathView
                  courseId={courseId}
                  batchId={batchId}
                  courseName={selected?.courseId?.name}
                />
              ))}
            {activeTab === "classes" && renderClasses()}
            {activeTab === "materials" && <MaterialsPanel batchId={batchId} />}
            {activeTab === "syllabus" && (
              <div className="ml-syllabus-embed">
                <StudentSyllabus batchId={batchId} compact />
              </div>
            )}
            {activeTab === "assignments" && <AssignmentsPanel batchId={batchId} />}
            {activeTab === "challenges" && <ChallengesPanel studentId={studentId} />}
          </div>

          {/* Right rail */}
          {showRail && (
            <aside className="ml-rail">
              {overviewLoading ? (
                <>
                  <div className="ml-widget ml-skeleton" style={{ height: 220 }} />
                  <div className="ml-widget ml-skeleton" style={{ height: 240 }} />
                  <div className="ml-widget ml-skeleton" style={{ height: 150 }} />
                </>
              ) : (
                <>
                  {/* Continue Learning */}
                  {cont && (
                    <div className="ml-widget">
                      <div className="ml-widget__head">
                        <h3 className="ml-widget__title">Continue Learning</h3>
                        <button type="button" className="ml-widget__link" onClick={() => setActiveTab("classes")}>
                          View All
                        </button>
                      </div>
                      <div className="ml-continue">
                        <div className="ml-continue__row">
                          <div className="ml-continue__thumb">
                            {cont.thumbnailUrl ? (
                              <img src={cont.thumbnailUrl} alt={cont.title} loading="lazy" />
                            ) : (
                              <Film size={18} />
                            )}
                          </div>
                          <div className="ml-continue__info">
                            <h4 className="ml-continue__title">{cont.title}</h4>
                            {cont.subtitle && <p className="ml-continue__sub">{cont.subtitle}</p>}
                          </div>
                        </div>
                        <div className="ml-continue__progress">
                          <div className="ml-continue__bar">
                            <div className="ml-continue__bar-fill" style={{ width: `${cont.percent || 0}%` }} />
                          </div>
                          <span className="ml-continue__pct">{cont.percent || 0}%</span>
                        </div>
                        <button
                          type="button"
                          className="ml-continue__cta"
                          onClick={() => cont.videoId && openRecording(cont.videoId)}
                        >
                          <Play size={15} />
                          Continue Watching
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Upcoming Classes */}
                  {upcoming.length > 0 && (
                    <div className="ml-widget">
                      <div className="ml-widget__head">
                        <h3 className="ml-widget__title">Upcoming Classes</h3>
                        <button type="button" className="ml-widget__link" onClick={() => navigate("/s/calendar")}>
                          View Calendar
                        </button>
                      </div>
                      <div className="ml-upcoming">
                        {upcoming.map((it) => {
                          const d = it.plannedDate ? new Date(it.plannedDate) : null;
                          return (
                            <div key={it.id} className="ml-upcoming__row">
                              <div className="ml-upcoming__date">
                                <span className="ml-upcoming__month">{d ? MONTHS[d.getMonth()] : ""}</span>
                                <span className="ml-upcoming__day">{d ? String(d.getDate()).padStart(2, "0") : ""}</span>
                              </div>
                              <div className="ml-upcoming__body">
                                <span className="ml-upcoming__title">{it.title}</span>
                                <span className="ml-upcoming__time">{formatTimeRange(it.startTime, it.endTime)}</span>
                              </div>
                              <button
                                type="button"
                                className="ml-upcoming__join"
                                onClick={() => {
                                  if (it.joinUrl) window.open(it.joinUrl, "_blank", "noopener");
                                  else navigate("/s/calendar");
                                }}
                              >
                                Join
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Quick Access */}
                  <div className="ml-widget">
                    <div className="ml-widget__head">
                      <h3 className="ml-widget__title">Quick Access</h3>
                    </div>
                    <div className="ml-quick">
                      {quickAccess.map(({ key, label, icon: Icon, token, go }) => (
                        <button key={key} type="button" className="ml-quick__tile" onClick={go}>
                          <span
                            className="ml-quick__icon"
                            style={{ background: `hsl(var(${token}) / 0.10)`, color: `hsl(var(${token}))` }}
                          >
                            <Icon size={18} />
                          </span>
                          <span className="ml-quick__label">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* My Progress */}
                  {progress && (
                    <div className="ml-widget">
                      <div className="ml-widget__head">
                        <h3 className="ml-widget__title">My Progress</h3>
                        <button type="button" className="ml-widget__link" onClick={() => navigate("/s/dashboard")}>
                          View Details
                        </button>
                      </div>
                      <div className="ml-progress">
                        <ProgressDonut overall={progress.overall || 0} segments={progressSegments} />
                        <ul className="ml-progress__legend">
                          {progressSegments.map((l) => (
                            <li key={l.key} className="ml-progress__item">
                              <span className="ml-progress__dot" style={{ background: l.color }} />
                              <span className="ml-progress__name">{l.label}</span>
                              <span className="ml-progress__count">{l.done} / {l.total}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              )}
            </aside>
          )}
        </div>
      </div>

      {/* ── Course-level video player (download disabled) ── */}
      <Modal
        show={!!playingCourseVideo}
        onHide={() => { setPlayingCourseVideo(null); setCvStreamUrl(null); }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-semibold">{playingCourseVideo?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {cvStreamLoading ? (
            <div className="ml-cvmodal__loading"><Spinner animation="border" /></div>
          ) : cvStreamUrl ? (
            <video
              src={cvStreamUrl}
              controls
              autoPlay
              preload="auto"
              controlsList="nodownload noremoteplayback"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              className="ml-cvmodal__player"
            />
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};

// Compact pagination model: 1 … (p-1) p (p+1) … last
function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

export default MyLearningPage;
